/**
 * Runs documentation page content.
 *
 * Creating runs (streaming + non-streaming), polling, follow-up messages,
 * cancellation, files, and permission handling.
 */

export const runsContent = `
A **run** is a single execution of a teammate. Runs stream events in real-time (tool calls, reasoning, output) and persist results.

## Create a Run

### Streaming (default)

The simplest way to get real-time output:

\`\`\`python
from m8tes import M8tes

client = M8tes()

for event in client.runs.create(teammate_id=1, message="Summarize the latest AI news"):
    print(event.type, event.raw)
\`\`\`

Use the context manager for automatic cleanup:

\`\`\`python
with client.runs.create(teammate_id=1, message="Summarize the latest AI news") as stream:
    for event in stream:
        pass
print(stream.text)  # Final output
\`\`\`

### Non-Streaming

For simpler use cases, disable streaming and poll for the result:

\`\`\`python
run = client.runs.create(
    message="Summarize the latest AI news",
    teammate_id=1,
    stream=False,
)
# Poll until complete
run = client.runs.poll(run.id)
print(run.output)
\`\`\`

\`\`\`bash
# Create (returns immediately with status="running")
curl -X POST https://m8tes.ai/api/v2/runs \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"teammate_id": 1, "message": "Summarize the latest AI news", "stream": false}'

# Poll until complete
curl https://m8tes.ai/api/v2/runs/1 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

### Quick Start Mode

Omit \`teammate_id\` to auto-create a teammate:

\`\`\`python
run = client.runs.create(
    message="Draft a weekly recap email",
    name="Weekly Bot",
    instructions="You write concise weekly recap emails",
    tools=["gmail"],
    stream=False,
)
run = client.runs.poll(run.id)
print(run.output)
\`\`\`

This finds or creates a teammate named "Weekly Bot" and runs it immediately.

### Run Options

| Field | Type | Default | Description |
|-------|------|---------|-------------|
| \`message\` | string (required) | — | The task or question for the teammate |
| \`teammate_id\` | int | — | Existing teammate to use |
| \`stream\` | bool | \`true\` | Enable real-time event streaming |
| \`tools\` | string[] | — | Override teammate tools for this run |
| \`name\` | string | — | Quick start: teammate name |
| \`instructions\` | string | — | Quick start: teammate instructions |
| \`user_id\` | string | — | End-user ID for [multi-tenancy](/docs/users) |
| \`metadata\` | dict | — | Arbitrary key-value data |
| \`memory\` | bool | \`true\` | Include saved per-user memories |
| \`history\` | bool | \`true\` | Include previous run context |
| \`permission_mode\` | string | \`"autonomous"\` | See [Permissions](/docs/permissions) |

## Follow-Up Messages

Continue a conversation with \`runs.reply()\`. This creates a new run that inherits the teammate and context from the original:

\`\`\`python
# Initial run
run = client.runs.create(teammate_id=1, message="Find open tickets", stream=False)
run = client.runs.poll(run.id)

# Follow up
for event in client.runs.reply(run.id, message="Now close the P2 tickets"):
    if hasattr(event, "delta"):
        print(event.delta, end="", flush=True)
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/runs/1/reply \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "Now close the P2 tickets"}'
\`\`\`

## Convenience Helpers

Shortcuts for common patterns:

\`\`\`python
# Create and wait for completion (no manual polling)
run = client.runs.create_and_wait(teammate_id=1, message="Generate weekly report")
print(run.output)

# Follow up and wait for completion
run = client.runs.reply_and_wait(run.id, message="Now email it to the team")
print(run.output)

# Stream only the text output (filters out tool events)
for chunk in client.runs.stream_text(teammate_id=1, message="Summarize news"):
    print(chunk, end="", flush=True)
\`\`\`

## List Runs

\`\`\`python
page = client.runs.list()
for run in page.data:
    print(run.id, run.status, run.output)

# Filter by teammate or status
page = client.runs.list(teammate_id=1, status="completed")
\`\`\`

\`\`\`bash
curl "https://m8tes.ai/api/v2/runs?teammate_id=1&status=completed" \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

## Get a Run

\`\`\`python
run = client.runs.get(run_id=1)
print(run.status, run.output)
\`\`\`

## Cancel a Run

\`\`\`python
run = client.runs.cancel(run_id=1)
print(run.status)  # "cancelled"
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/runs/1/cancel \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

## Run Files

Agents can generate files during execution. List and download them after the run completes:

\`\`\`python
files = client.runs.list_files(run_id=1)
for f in files:
    print(f.name, f.size)

# Download a specific file
content = client.runs.download_file(run_id=1, filename="report.csv")
with open("report.csv", "wb") as f:
    f.write(content)
\`\`\`

\`\`\`bash
# List files
curl https://m8tes.ai/api/v2/runs/1/files \\
  -H "Authorization: Bearer m8_your_key_here"

# Download a file
curl https://m8tes.ai/api/v2/runs/1/files/report.csv/download \\
  -H "Authorization: Bearer m8_your_key_here" -o report.csv
\`\`\`

## Run Statuses

| Status | Description |
|--------|-------------|
| \`running\` | Teammate is actively executing |
| \`completed\` | Finished successfully, output available |
| \`failed\` | Execution failed, check \`run.error\` |
| \`cancelled\` | Cancelled by user |
| \`awaiting_approval\` | Paused — tool needs [permission approval](/docs/permissions) |

## What's Next

- [Streaming & Events](/docs/streaming) — event types and real-time patterns
- [Permissions](/docs/permissions) — control tool approval flow
- [Files](/docs/files) — working with teammate-generated files
`;
